import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack
} from '@mui/material';
import DataTable from '../../../components/design/DataTable';
import LoadingState from '../../../components/design/LoadingState';
import EmptyState from '../../../components/design/EmptyState';
import externalApiService from '../../../services/externalApiService';

const SERVICE_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' }
];

const CATEGORY_OPTIONS = [
  { value: 'billing', label: 'Cobrança' },
  { value: 'otp', label: 'OTP' },
  { value: 'service', label: 'Serviço' }
];

function maskToken(preview) {
  const p = String(preview || '');
  return p ? `••••••${p}` : '—';
}

export default function ExternalApiIntegrationsPage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [tokenDialog, setTokenDialog] = useState({ open: false, token: '', integrationId: null });
  const [editDialog, setEditDialog] = useState({ open: false, mode: 'create', row: null });
  const [form, setForm] = useState({ name: '', clientId: '', secretId: '', serviceChannel: 'whatsapp', messageCategory: 'service', status: 'active', linkToTicket: false, rateLimitPerMinute: 120, maxConcurrency: 5, dailyLimit: '' });
  const [tokenCache, setTokenCache] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const resp = await externalApiService.listIntegrations();
      const data = resp?.data || resp?.data?.data || resp?.data?.rows || resp?.data?.integrations || resp?.data?.data?.integrations || resp?.data?.data || resp?.data;
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setRows(list);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const openToken = (token, integrationId) => setTokenDialog({ open: true, token, integrationId });
  const closeToken = () => setTokenDialog({ open: false, token: '', integrationId: null });

  const openCreate = () => {
    setEditDialog({ open: true, mode: 'create', row: null });
    setForm({ name: '', clientId: '', secretId: '', serviceChannel: 'whatsapp', messageCategory: 'service', status: 'active', linkToTicket: false, rateLimitPerMinute: 120, maxConcurrency: 5, dailyLimit: '' });
  };

  const openEdit = (row) => {
    setEditDialog({ open: true, mode: 'edit', row });
    setForm({
      name: row?.name || '',
      clientId: row?.clientId || '',
      secretId: row?.secretId || '',
      serviceChannel: row?.serviceChannel || 'whatsapp',
      messageCategory: row?.messageCategory || 'service',
      status: row?.status || 'active',
      linkToTicket: Boolean(row?.linkToTicket),
      rateLimitPerMinute: Number(row?.rateLimitPerMinute ?? 120),
      maxConcurrency: Number(row?.maxConcurrency ?? 5),
      dailyLimit: row?.dailyLimit === null || row?.dailyLimit === undefined ? '' : String(row.dailyLimit)
    });
  };

  const closeEdit = () => setEditDialog({ open: false, mode: 'create', row: null });

  const save = async () => {
    setLoading(true);
    try {
      if (editDialog.mode === 'create') {
        const resp = await externalApiService.createIntegration({
          name: form.name,
          clientId: form.clientId || undefined,
          secretId: form.secretId || undefined,
          serviceChannel: form.serviceChannel,
          messageCategory: form.messageCategory,
          status: form.status,
          linkToTicket: form.linkToTicket,
          rateLimitPerMinute: Number(form.rateLimitPerMinute || 120),
          maxConcurrency: Number(form.maxConcurrency || 5),
          dailyLimit: String(form.dailyLimit || '').trim() ? Number(form.dailyLimit) : null
        });
        const token = resp?.data?.token || resp?.token || resp?.data?.data?.token || resp?.data?.data?.token;
        const integration = resp?.data?.integration || resp?.data?.data?.integration || resp?.data?.data?.integration;
        if (token && integration?.id) {
          setTokenCache(prev => ({ ...prev, [String(integration.id)]: token }));
          openToken(token, integration.id);
        } else if (token) {
          openToken(token, null);
        }
      } else {
        await externalApiService.updateIntegration(editDialog.row.id, {
          name: form.name,
          clientId: form.clientId,
          secretId: form.secretId,
          serviceChannel: form.serviceChannel,
          messageCategory: form.messageCategory,
          status: form.status,
          linkToTicket: form.linkToTicket,
          rateLimitPerMinute: Number(form.rateLimitPerMinute || 120),
          maxConcurrency: Number(form.maxConcurrency || 5),
          dailyLimit: String(form.dailyLimit || '').trim() ? Number(form.dailyLimit) : null
        });
      }
      closeEdit();
      await load();
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (row) => {
    setLoading(true);
    try {
      const next = row.status === 'active' ? 'inactive' : 'active';
      await externalApiService.updateIntegration(row.id, { status: next });
      await load();
    } finally {
      setLoading(false);
    }
  };

  const doRegenerate = async (row) => {
    setLoading(true);
    try {
      const resp = await externalApiService.regenerateToken(row.id);
      const token = resp?.data?.token || resp?.data?.data?.token || resp?.token;
      if (token) {
        setTokenCache(prev => ({ ...prev, [String(row.id)]: token }));
        openToken(token, row.id);
      }
      await load();
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { key: 'name', label: 'Nome' },
    { key: 'companyName', label: 'Empresa' },
    {
      key: 'serviceChannel',
      label: 'Serviço',
      render: (v) => SERVICE_OPTIONS.find(item => item.value === v)?.label || v || '—'
    },
    {
      key: 'messageCategory',
      label: 'Tipo',
      render: (v) => CATEGORY_OPTIONS.find(item => item.value === v)?.label || v || '—'
    },
    {
      key: 'status',
      label: 'Status',
      render: (v) => <Chip size="small" color={v === 'active' ? 'success' : 'default'} label={v === 'active' ? 'Ativa' : 'Inativa'} />
    },
    { key: 'token', label: 'Preview do token', render: (_, r) => maskToken(r.tokenPreview) },
    { key: 'lastUsedAt', label: 'Último uso', render: (v) => v ? new Date(v).toLocaleString() : '—' },
    { key: 'totalSends', label: 'Total envios', align: 'right' },
    { key: 'totalFailures', label: 'Falhas', align: 'right' },
    {
      key: 'actions',
      label: 'Ações',
      render: (_, r) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" variant="outlined" onClick={() => openEdit(r)}>Editar</Button>
          <Button size="small" variant="outlined" onClick={() => toggleStatus(r)}>
            {r.status === 'active' ? 'Desativar' : 'Ativar'}
          </Button>
          <Button size="small" variant="contained" onClick={() => doRegenerate(r)}>Regenerar token</Button>
          <Button
            size="small"
            variant="text"
            disabled={!tokenCache[String(r.id)]}
            onClick={() => openToken(tokenCache[String(r.id)], r.id)}
          >
            Ver token
          </Button>
        </Stack>
      ),
      align: 'right'
    }
  ];

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(String(tokenDialog.token || ''));
    } catch (_) {}
  };

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h5">Integrações ERP</Typography>
          <Typography variant="body2" color="text.secondary">
            O token bruto da integração deve ser enviado no header <strong>Authorization: Bearer SEU_TOKEN</strong>. O valor <strong>token</strong> no body do endpoint OTP é apenas o código que será entregue ao cliente.
          </Typography>
        </Box>
        <Button variant="contained" onClick={openCreate}>Criar integração</Button>
      </Box>

      {loading ? (
        <LoadingState label="Carregando integrações..." />
      ) : rows.length ? (
        <DataTable
          columns={columns}
          rows={rows.map(r => ({ ...r, token: r.tokenPreview }))}
          getKey={(r) => r.id}
        />
      ) : (
        <Paper>
          <EmptyState title="Nenhuma integração cadastrada" actionLabel="Criar integração" onAction={openCreate} />
        </Paper>
      )}

      <Dialog open={editDialog.open} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>{editDialog.mode === 'create' ? 'Criar integração' : 'Editar integração'}</DialogTitle>
        <DialogContent>
          <Box mt={1} display="grid" gap={2}>
            <TextField
              label="Nome"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="ClientId"
              value={form.clientId}
              onChange={(e) => setForm(prev => ({ ...prev, clientId: e.target.value }))}
              fullWidth
            />
            <TextField
              label="SecretId"
              value={form.secretId}
              onChange={(e) => setForm(prev => ({ ...prev, secretId: e.target.value }))}
              fullWidth
            />
            <TextField
              select
              label="Serviço"
              value={form.serviceChannel}
              onChange={(e) => setForm(prev => ({ ...prev, serviceChannel: e.target.value }))}
              fullWidth
            >
              {SERVICE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Tipo"
              value={form.messageCategory}
              onChange={(e) => setForm(prev => ({ ...prev, messageCategory: e.target.value }))}
              fullWidth
            >
              {CATEGORY_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Rate limit (req/min)"
              type="number"
              value={form.rateLimitPerMinute}
              onChange={(e) => setForm(prev => ({ ...prev, rateLimitPerMinute: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Concorrência máx (worker)"
              type="number"
              value={form.maxConcurrency}
              onChange={(e) => setForm(prev => ({ ...prev, maxConcurrency: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Limite diário (opcional)"
              type="number"
              value={form.dailyLimit}
              onChange={(e) => setForm(prev => ({ ...prev, dailyLimit: e.target.value }))}
              fullWidth
            />
            <FormControlLabel
              control={<Switch checked={form.linkToTicket} onChange={(e) => setForm(prev => ({ ...prev, linkToTicket: e.target.checked }))} />}
              label="Vincular envio a ticket"
            />
            <FormControlLabel
              control={<Switch checked={form.status === 'active'} onChange={(e) => setForm(prev => ({ ...prev, status: e.target.checked ? 'active' : 'inactive' }))} />}
              label="Ativa"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={!String(form.name || '').trim()}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tokenDialog.open} onClose={closeToken} maxWidth="sm" fullWidth>
        <DialogTitle>Token da integração</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Este token é exibido apenas agora. Armazene com segurança.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use exatamente este valor no header <strong>Authorization</strong>, no formato <strong>Bearer SEU_TOKEN</strong>.
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
              {tokenDialog.token}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeToken}>Fechar</Button>
          <Button variant="contained" onClick={copyToken}>Copiar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
